/**
 * The speech-following model shared by main, preload and the renderer (ticket 14,
 * FR-31 to FR-38).
 *
 * Like `recording.ts`, this is the part that belongs to neither process. The
 * microphone can only be opened in a renderer; the recogniser is a Node native
 * addon and can only be loaded in main. So the fork crosses the process boundary,
 * and everything about it that can be decided without either a microphone or a
 * model is stated here once and unit-tested in Node.
 *
 * It is also the validation boundary for audio frames, which arrive from a
 * renderer over IPC — {@link asSpeechFrame} takes `unknown` and discards what does
 * not fit rather than trusting the shape.
 */

import type { RecordingPhase } from "./recording.js";
import { isRecordingActive } from "./recording.js";
import type { ScrollMode } from "./scroll-mode.js";

/**
 * The sample rate the microphone fork is resampled to before it reaches the
 * recogniser.
 *
 * Fixed by the model, not by the hardware: the streaming Zipformer extracts
 * features at 16 kHz, so anything else is resampled anyway. Doing it once in the
 * renderer's `AudioContext` — which resamples in native code as part of the audio
 * graph — is cheaper than shipping 48 kHz frames across IPC to be resampled in
 * main, and it cuts the bytes crossing the boundary by two thirds.
 */
export const MIC_SAMPLE_RATE = 16_000;

/**
 * How many samples each forked frame carries — 2048 at 16 kHz, so 128 ms of
 * audio.
 *
 * The M0 gate was measured at 100 ms frames and cleared a 700 ms median-latency
 * bar with 416 ms (ADR-0001), so frame quantisation is not where the latency
 * budget goes. Mean quantisation delay is half a frame, which puts the whole
 * prize for moving to 100 ms at about 14 ms — a fraction of what shortening one
 * CSS transition was worth (ADR-0020) — against a 28% rise in the message rate on
 * the hot path in both hosts. Not worth taking.
 *
 * It does *not* have to be a power of two, which is what this note used to say.
 * That was true of the `ScriptProcessorNode` the fork was originally built on; the
 * fork is an `AudioWorklet` now (one-window ticket 04), and a worklet accumulates
 * 128-sample render quanta into a buffer of any size at all — 1600 is perfectly
 * available. The reason to stay at 2048 is the 14 ms, not the arithmetic.
 */
export const MIC_FRAME_SAMPLES = 2048;

/**
 * How much audio one forked frame represents.
 *
 * This is how main timestamps the frames it hands the follower: by counting them,
 * not by reading a wall clock. The `Follower` contract asks for a time in the
 * recording's own clock, and deriving it from the audio is the only definition
 * that stays right across a paused take, a stalled renderer or a system clock that
 * steps. (The chosen streaming recogniser happens to ignore the timestamp and work
 * purely from samples; the harness's reference followers do not, and the contract
 * is shared.)
 */
export const MIC_FRAME_MS = (MIC_FRAME_SAMPLES / MIC_SAMPLE_RATE) * 1_000;

/**
 * The name the audio-worklet processor registers under, and the name the page
 * constructs the node by (FR-31).
 *
 * Here rather than beside either host's worklet, and not for tidiness. A worklet
 * module is an *entry point*: it calls `registerProcessor` when it is evaluated,
 * which is the whole of what it is for, and on the audio thread that is right
 * and in a page it is a `ReferenceError` that takes the bundle down with it. So
 * the name the two halves share cannot live in the worklet — and both hosts have
 * a worklet of their own, so it should not have to be written twice either.
 */
export const MIC_PROCESSOR = "cuekards-mic";

/** What the audio thread posts back to the page that attached it. */
export type MicMessage =
  | { kind: "frame"; samples: Float32Array }
  /** The rate the graph actually got. Asked for at the model's 16 kHz so the
   * browser resamples in native code — but a browser may give its own, and
   * frames at the wrong rate are a recogniser hearing a chipmunk. */
  | { kind: "rate"; sampleRate: number };

/**
 * The speech follower's state, broadcast to both windows on every change.
 *
 * Deliberately separate from `RecordingStatus`, because the two are independent:
 * the follower runs while recording *and* on its own for rehearsal (FR-37), and a
 * follower that cannot start must not be able to stop a take.
 */
export interface FollowerState {
  /** Whether microphone audio is currently reaching the follower. */
  listening: boolean;
  /** Whether a rehearsal has been asked for — the follower running with no take
   * in progress (FR-37). Stays true across a take started mid-rehearsal, so
   * stopping the take returns to rehearsing rather than to silence. */
  rehearsing: boolean;
  /** Whether the recogniser is loaded and able to follow. False before the first
   * script loads, and after a failure. */
  ready: boolean;
  /** Why the follower is unavailable, if it is. Speech-following degrades to a
   * conventional prompter rather than breaking the app (PRD goal G5), so this is
   * reported and then lived with. */
  error?: string;
  /**
   * How far behind real time the recogniser is running, in milliseconds:
   * wall-clock elapsed since the first frame, minus the audio actually decoded.
   * `undefined` before anything has been heard.
   *
   * **This is a diagnostic, and it exists because there was no way to tell two
   * very different problems apart.** The harness scores latency in the recording's
   * own clock, feeding frames as fast as they decode — a fair measure of how long
   * the model takes to commit to a word, and blind by construction to whether the
   * live app can keep up with a person talking. Those two failures feel identical
   * at the desk ("the prompter is behind me") and have nothing in common as fixes.
   *
   * Near zero and steady: the app is keeping up and what is left is the model's
   * own lookahead, which no amount of pipeline work will shorten — that is a
   * different model, or accepting it. Climbing: audio is arriving faster than it
   * is being consumed, every second of reading puts the prompter further behind,
   * and the fix is somewhere in this process.
   */
  backlogMs?: number;
}

/** The follower's state before a script has been loaded or a microphone opened. */
export const IDLE_FOLLOWER: FollowerState = {
  listening: false,
  rehearsing: false,
  ready: false,
};

/**
 * How the follower's state reads in the control window, where there is room for a
 * phrase and the user is at the keyboard deciding whether to press record.
 *
 * Every state names the feature rather than leaving it to be inferred. A bare
 * "Unavailable" or "Ready" above a record button reads as a claim about the
 * camera, or about the take, and the one thing this line is for is the follower.
 * `Listening` is the exception because it is the only one said mid-take, and by
 * then there is nothing else it could be about.
 */
export function followerStatusLabel(state: FollowerState): string {
  if (state.error !== undefined) return FOLLOW_VOICE_MISSING;
  if (state.listening) {
    const what = state.rehearsing ? "Listening (rehearsal)" : "Listening";
    return `${what}${backlogSuffix(state)}`;
  }
  if (!state.ready) return "No script loaded";
  return "Follow my voice ready";
}

/** The follower cannot run. Exported because the model-aware label in
 * `model-download/offer.ts` says it too, for a model that has not arrived. */
export const FOLLOW_VOICE_MISSING = "Follow my voice missing";

/** The follower could run, but the scroll mode is not following anybody. */
export const FOLLOW_VOICE_OFF = "Follow my voice off";

/**
 * The phone's follower chip, which sits beside the mode chip and so can say "off".
 *
 * {@link followerStatusLabel} sees only the follower, and a follower in a
 * prompter set to `manual` or `constant` would still claim to be ready (or
 * missing) for a script it is not following. The order is the web line's in
 * `model-download/offer.ts`: what it hears mid-take first, then a recogniser
 * that is not there, then the mode. `missing` is the host's own word that the
 * model is not on the device, which the follower alone cannot know before it has
 * been handed a script.
 */
export function followerChipLabel(
  state: FollowerState,
  mode: ScrollMode,
  missing: boolean,
): string {
  if (state.listening) return followerStatusLabel(state);
  if (missing || state.error !== undefined) return FOLLOW_VOICE_MISSING;
  if (mode !== "speech") return FOLLOW_VOICE_OFF;
  return followerStatusLabel(state);
}

/**
 * The rehearse control's label, for the transport bar it now sits in
 * (scroll-and-bar ticket 01).
 *
 * It carries the backlog because the transport has one status slot and it belongs
 * to the take. Of everything {@link followerStatusLabel} says, the backlog is the
 * part worth keeping here: whether the follower is keeping up is the entire
 * question a rehearsal is asked to answer, and it is the one thing you cannot see
 * by looking at the prompter. The rest of that label's cases — unavailable, no
 * script — say why the control cannot be pressed rather than what it would do, so
 * they belong to {@link rehearseBlocked} instead.
 *
 * The glyphs stay glyphs. `icons.tsx` replaced them everywhere the label went
 * away, on its own argument that a character cannot be sized or coloured apart
 * from its label — which does not apply where there still is one, and this is a
 * text link.
 */
export function rehearseLabel(state: FollowerState): string {
  if (!state.rehearsing) return "▶ Rehearse";
  return `■ Stop rehearsing${backlogSuffix(state)}`;
}

/**
 * Why the rehearse control cannot be pressed, or null when it can.
 *
 * A reason rather than a boolean, so the `disabled` attribute and the hover text
 * come from one decision. This is the same seam `transportButtons` exists at and
 * for the same reason: the row has no spare width for an explanation, so what a
 * disabled control means lives in a `title`, and a `title` that disagrees with the
 * `disabled` beside it looks like nothing at all on screen.
 */
export function rehearseBlocked(
  state: FollowerState,
  phase: RecordingPhase,
  /**
   * Whether the user has switched the microphone off (web-ui ticket 06).
   *
   * Optional, and false where a host does not have the switch — the desktop's
   * settings modal grew it at the same time, but a caller that has not been taught
   * about it should get the answer it always got rather than a compile error about
   * a device.
   */
  micOff = false,
): string | null {
  /**
   * The take's own audio is already reaching the follower (FR-37), so there is
   * nothing a rehearsal would add and pressing it would only be confusing.
   *
   * **Unless the take has no microphone in it**, which since web-ui ticket 06 it
   * can. `CaptureEngine`'s `#open` does not fork a stream with no audio track —
   * there is nothing to fork — so during a camera-only take the follower is not
   * running on anything, and this sentence would sit directly above
   * `MIC_OFF_NOTE` saying the opposite of it. Still refused, because a rehearsal
   * cannot start over a microphone that is off either; the switch is simply the
   * true reason.
   */
  if (isRecordingActive(phase)) {
    return micOff
      ? MIC_OFF_REASON
      : "The follower is already running on the take's own audio";
  }
  /**
   * A microphone the user has switched off, which is a rehearsal that cannot
   * start (web-ui ticket 06).
   *
   * The engine refuses it and is right to: a preview is authoritative about the
   * device it has switched off, including against a rehearsal — see
   * `CaptureEngine`'s `#wanted`. What was missing is anyone saying so on the way
   * *in*. `startRehearsal` records `rehearsing: true` the moment it is called, and
   * an engine that opens nothing sends neither `listening` nor `listenFailed`, so
   * the control latched to "■ Stop rehearsing" over a microphone that was never
   * going to open — a press that did nothing and then reported that it had.
   *
   * Answered here rather than by a `listenFailed` from the engine, and the
   * difference is how long the two last: `state.error` latches until a script is
   * reloaded, which would leave Rehearse dead long after the microphone came back.
   * This is derived from the switch, so it clears itself when the switch does.
   *
   * **Only where there is no rehearsal already running.** This is one control for
   * two verbs, and only the starting one is refused: a rehearsal begun over a live
   * microphone that was switched off afterwards is still *standing* — the engine
   * keeps `#rehearsalWanted` and resumes the moment the switch comes back — so a
   * row that dimmed here would be one the user cannot cancel, reading "■ Stop
   * rehearsing" with no way to stop it.
   *
   * Ahead of the error below: a missing model is a thing to go and fix, and a
   * microphone switched off is the reason the press in front of you did nothing.
   */
  if (micOff && !state.rehearsing) return MIC_OFF_REASON;
  // Stated rather than merely greyed: without the recogniser cuekards is still a
  // working teleprompter (goal G5), and the reader needs to know that is what
  // they now have. The error itself also reaches `.transport-error` in full.
  if (state.error !== undefined) return state.error;
  if (!state.ready) return "Load a script to rehearse against it";
  return null;
}

/** Why rehearsal is refused over a switched-off microphone. Exported because two
 * hosts draw the row and a third string of the same sentence is how they drift. */
export const MIC_OFF_REASON = "The microphone is switched off";

/**
 * Why the microphone cannot be opened just to look at its level, or null when it
 * can (mobile-app ticket 29).
 *
 * Beside {@link rehearseBlocked} rather than a parameter to it, because it is a
 * smaller question and not a variant of the same one. A rehearsal needs a
 * recogniser and a script to follow; a level meter needs an open device and
 * nothing else. The phone's green room asked the rehearsal's question of its
 * "Check the microphone" button, and so the one reader the check is for —
 * nothing downloaded, nothing started — was the one who could not press it.
 *
 * What is left is what is true of the *device*: a take owns it (FR-31, one
 * open), and a microphone that is switched off is not there to be opened. A
 * refused permission is deliberately not here — it is the answer to a press, and
 * the way out of it is another press after a trip to Settings.
 */
export function micCheckBlocked(
  phase: RecordingPhase,
  micOff = false,
  /** Whether a check is already running. One control for two verbs, as the
   * rehearsal's is, and only the starting one is refused by the switch. */
  checking = false,
): string | null {
  if (isRecordingActive(phase)) {
    return micOff ? MIC_OFF_REASON : MIC_CHECK_TAKE_REASON;
  }
  if (micOff && !checking) return MIC_OFF_REASON;
  return null;
}

/** Why the check is refused while a take is open. */
export const MIC_CHECK_TAKE_REASON = "The take is using the microphone";

/** What pressing the rehearse control would do, when it can be pressed. */
export const REHEARSE_TITLE = "Follow along without recording";

/** The backlog as a suffix, or nothing when there is none worth saying. Shared by
 * {@link followerStatusLabel} and {@link rehearseLabel} so the two cannot report
 * the same number differently. */
function backlogSuffix(state: FollowerState): string {
  // Only once it is worth saying. Below a couple of frames this is scheduling
  // noise, and a number that twitches around zero all take reads as a fault.
  if (state.backlogMs === undefined || state.backlogMs < BACKLOG_WARN_MS) {
    return "";
  }
  return ` · ${Math.round(state.backlogMs / 100) / 10}s behind`;
}

/**
 * The backlog worth putting on screen: a third of a second, or about two and a
 * half frames.
 *
 * Below this it is jitter — a frame that waited on a garbage collection, an IPC
 * hop that landed late — and it recovers on its own. At this much the recogniser
 * is a word behind where the microphone is and not catching up, which the reader
 * can see for themselves and should be able to name.
 */
export const BACKLOG_WARN_MS = 333;

/**
 * Narrow an unvalidated audio frame from a renderer to its samples, or null when
 * it is not one.
 *
 * Electron's structured clone can deliver the samples as a typed array or as a
 * raw `ArrayBuffer` depending on how the renderer handed them over, and a frame
 * arriving as `Int16Array` would be silently misread as garbage audio — so the
 * view is required to be a `Float32Array` specifically rather than any view.
 * Empty frames are rejected: feeding the recogniser nothing costs a decode pass
 * and tells it the speaker fell silent.
 */
export function asSpeechFrame(value: unknown): Float32Array | null {
  if (value instanceof Float32Array) {
    return value.length > 0 ? value : null;
  }
  if (value instanceof ArrayBuffer) {
    // A whole buffer carries no element type, so the only sound reading is the
    // one both sides agreed on.
    if (value.byteLength === 0 || value.byteLength % 4 !== 0) return null;
    return new Float32Array(value);
  }
  return null;
}
